import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumn } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class ClusterKMeans extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.clusterkmeans',
        group: 'Spatial Analysis',
        title: 'ST Cluster K-Means',
        description: `
      This component performs a K-means clustering.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'nclusters',
          type: WorkflowComponentParamType.Number,
          title: 'Number of clusters',
          default: 3,
          min: 1,
          optional: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    let query = ''
    switch (connection.type) {
      case Provider.BigQuery: {
        const select = dedent`
          WITH __clusters AS
          (
            SELECT c.cluster, c.geom, ST_ASBINARY(c.geom) AS geom_bin
            FROM (
              SELECT
                ${connection.quoteName(
                  envVars.analyticsToolboxDataset as string
                )}.ST_CLUSTERKMEANS(
                  __pts,
                  ${preparedParams.nclusters ?? 'NULL'}
                ) __clustandgeom
              FROM (
                SELECT ARRAY_AGG(${preparedParams.geo}) __pts
                FROM ${connection.quoteName(preparedParams.source as string)}
              )
            ) clusters, UNNEST(clusters.__clustandgeom) c
          ),
          __result AS
          (
            SELECT ANY_VALUE(geom) AS geom, ANY_VALUE(cluster) AS cluster
            FROM __clusters
            GROUP BY geom_bin
          )
          SELECT __result.cluster, __source.*
          FROM
            __result,
            ${connection.quoteName(preparedParams.source as string)} AS __source
            WHERE
            ST_ASTEXT(__result.geom) = ST_ASTEXT(__source.${
              preparedParams.geo
            })`

        query = connection.getCreateTempTableQuery(
          outputTablename,
          select,
          codeType,
          null
        )
        break
      }
      case Provider.Snowflake: {
        const select = dedent`WITH __result AS
        (
                SELECT
                  GET(VALUE, 'cluster') AS cluster, TO_GEOGRAPHY(GET(VALUE, 'geom')) AS geom
                FROM (
                  SELECT
                    ${connection.quoteName(
                      envVars.analyticsToolboxDataset as string
                    )}.ST_CLUSTERKMEANS(
                      __pts,
                      ${preparedParams.nclusters ?? 3}
                    ) __clustandgeom
                  FROM (
                    SELECT ARRAY_AGG(ST_ASGEOJSON(${
                      preparedParams.geo
                    })::STRING) __pts
                    FROM ${connection.quoteName(
                      preparedParams.source as string
                    )}
                  )
                ) clusters, lateral FLATTEN(input => __clustandgeom)
              )
              SELECT __result.cluster, __source.*
              FROM
                __result,
                ${connection.quoteName(
                  preparedParams.source as string
                )} AS __source
              WHERE
                ST_X(__result.geom) = ST_X(__source.${preparedParams.geo})
                AND
                ST_Y(__result.geom) = ST_Y(__source.${preparedParams.geo})`
        query = connection.getCreateTempTableQuery(
          outputTablename,
          select,
          codeType,
          null
        )
        break
      }
      case Provider.Postgres: {
        const select = dedent`SELECT ST_ClusterKMeans(
                  ${preparedParams.geo},
                  ${preparedParams.nclusters ?? 3}
                ) OVER () AS cluster, *
              FROM ${connection.quoteName(preparedParams.source as string)}`
        query = connection.getCreateTempTableQuery(
          outputTablename,
          select,
          codeType,
          null
        )
        break
      }
      case Provider.Redshift: {
        const temptable = tempTableName(componentName, preparedParams.source)
        const select = dedent`WITH __result AS
                (
                  SELECT c.cluster AS cluster, ST_GEOMFROMGEOJSON(JSON_SERIALIZE(c.geom)) AS geom
                  FROM ${temptable} clusters, clusters.__clustandgeom c
                )
                SELECT __result.cluster, __source.*
                FROM
                  __result,
                  ${connection.quoteName(
                    preparedParams.source as string
                  )} AS __source
                WHERE
                  ST_X(__result.geom) = ST_X(__source.${preparedParams.geo})
                  AND
                  ST_Y(__result.geom) = ST_Y(__source.${preparedParams.geo})`

        const createTable = connection.getCreateTempTableQuery(
          outputTablename,
          select,
          codeType,
          null
        )
        query = dedent`
            BEGIN
              EXECUTE '
                CREATE TABLE ${temptable} AS
                SELECT
                ${connection.quoteName(
                  envVars.analyticsToolboxDataset as string
                )}.ST_CLUSTERKMEANS(
                  __pts,
                  ${preparedParams.nclusters ?? 3}
                ) __clustandgeom
                FROM (
                  SELECT ST_COLLECT(${preparedParams.geo}) __pts
                  FROM ${connection.quoteName(preparedParams.source as string)}
                )';
              ${createTable}
              DROP TABLE ${temptable};
            END;`
        break
      }
      default:
        return null
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `cluster`,
      type: ColumnDataType.Number
    })
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Snowflake,
      Provider.Redshift,
      Provider.Postgres
    ]
  }
}

export { ClusterKMeans }
